import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@emotion/react';
import { tokens } from '../../theme';
import RoleChip from '../../SystemDesign/RoleChip';
import DateInTable from '../../SystemDesign/DateInTable';
import { formattedDate } from '../../Utils/Functions';
import { useGetAttemptsQuery, useGetAllQuizResponsesQuery, useGetQuizzesQuestionsQuery } from '../../services/SkillsManagement';

// Subcomponent: fetch responses for a specific attempt and render table rows
const AttemptResponsesTable = ({ quizId, userId, attemptId, questionsIndex }) => {
  // Local theme colors for chips
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { data, isLoading } = useGetAllQuizResponsesQuery(
    { quizId, userId, attemptId, page: 1, limit: 1000 },
    { refetchOnMountOrArgChange: true, skip: !attemptId || !quizId || !userId }
  );
  const rows = React.useMemo(() => data?.data?.quizResponses || [], [data]);

  const getQuestion = (qid) => questionsIndex.get(qid) || {};

  // Show label 'Correct' / 'Incorrect' as requested
  const getCorrectLabel = (r) => (r?.isCorrect ? 'Correct' : 'Incorrect');

  const getUserAnswerText = (r) => {
    if (r?.textResponse) return r.textResponse;
    // Prefer backend-expanded option text if present
    if (r?.option?.optionText) return r.option.optionText;
    // Fallback to questions index
    const q = getQuestion(r?.questionId);
    const opts = Array.isArray(q?.options) ? q.options : [];
    const opt = opts.find((o) => Number(o?.optionId || o?.id) === Number(r?.optionId));
    return opt?.optionText || opt?.text || (r?.optionId ? `Option #${r.optionId}` : '-');
  };

  const totalPoints = rows.reduce((acc, r) => acc + Number(r?.pointsEarned || 0), 0);

  if (isLoading) return <Typography variant="body2">Loading responses...</Typography>;

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Question</TableCell>
            <TableCell>Your Answer</TableCell>
            <TableCell>Result</TableCell>
            <TableCell align="right">Points</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, idx) => {
            const q = getQuestion(r.questionId);
            return (
              <TableRow key={r.responseId || idx}>
                <TableCell>{idx + 1}</TableCell>
                <TableCell>{r?.question?.questionText || q?.questionText || q?.text || `Q#${r.questionId}`}</TableCell>
                <TableCell>{getUserAnswerText(r)}</TableCell>
                <TableCell>
                  {(() => {
                    const isOk = !!r?.isCorrect;
                    const label = isOk ? 'Correct' : 'Incorrect';
                    const colorOverride = isOk ? colors.green[700] : colors.redAccent[600];
                    const bgColorOverride = isOk ? colors.green[100] : colors.redAccent[100];
                    return <RoleChip role={label} colorOverride={colorOverride} bgColorOverride={bgColorOverride} />;
                  })()}
                </TableCell>
                <TableCell align="right">{Number(r?.pointsEarned || 0)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Typography variant="subtitle2">Total score: {totalPoints}</Typography>
      </Box>
    </>
  );
};

// Main dialog: Review all attempts for a quiz
const ReviewAllAttempts = ({ open, onClose, quizId, userId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { data: attemptsData, isLoading: loadingAttempts } = useGetAttemptsQuery(
    { quizId, userId, page: 1, limit: 100 },
    { refetchOnMountOrArgChange: true, skip: !quizId || !userId }
  );

  // Fetch questions once per quiz to resolve text and correct options
  const { data: questionsData } = useGetQuizzesQuestionsQuery(
    { quizId, page: 1, limit: 1000 },
    { refetchOnMountOrArgChange: true, skip: !quizId }
  );

  const attempts = React.useMemo(() => attemptsData?.data?.quizAttempts || [], [attemptsData]);
  const questionsIndex = React.useMemo(() => {
    const list = questionsData?.data?.questions || [];
    const map = new Map();
    list.forEach((q) => map.set(q?.questionId || q?.id, q));
    return map;
  }, [questionsData]);

  const quizTitle = attempts[0]?.quiz?.title || '';
  const timeLimit = attempts[0]?.quiz?.timeLimit || null;
  const attemptsCount = attempts.length;

  // Helper to convert seconds to minutes + seconds label
  const fmtSeconds = (secs) => {
    if (secs == null) return '-';
    const s = Number(secs) || 0;
    const m = Math.floor(s / 60);
    const rs = s % 60;
    if (m > 0) return `${m} min${rs ? ` ${rs} s` : ''}`;
    return `${rs} s`;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Quiz Report</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loadingAttempts ? (
          <Typography variant="body2">Loading attempts...</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Quiz summary */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 2 }}>
              <Box>
                <Typography variant="caption" color={colors.grey[400]}>Title</Typography>
                <Typography variant="body1">{quizTitle || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color={colors.grey[400]}>Time limit</Typography>
                <Typography variant="body1">{timeLimit != null ? `${timeLimit} min` : '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color={colors.grey[400]}>Attempts</Typography>
                <Typography variant="body1">{attemptsCount}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color={colors.grey[400]}>Last status</Typography>
                {attempts[0]?.status ? (
                  (() => {
                    const s = String(attempts[0].status).toLowerCase();
                    let colorOverride = colors.green[700];
                    let bgColorOverride = colors.green[100];
                    let label = s;
                    if (s === 'completed') { label = 'completed'; }
                    else if (s === 'in_progress' || s === 'in progress') { label = 'in progress'; colorOverride = colors.Orange[400]; bgColorOverride = colors.Orange[100]; }
                    else if (s === 'expired') { label = 'expired'; colorOverride = colors.redAccent[600]; bgColorOverride = colors.redAccent[100]; }
                    return <RoleChip role={label} colorOverride={colorOverride} bgColorOverride={bgColorOverride} />;
                  })()
                ) : '-' }
              </Box>
              <Box>
                <Typography variant="caption" color={colors.grey[400]}>Last attempt at</Typography>
                <Typography variant="body1">
                  {attempts[0]?.updatedAt || attempts[0]?.endTime || attempts[0]?.createdAt ? (
                    <DateInTable date={formattedDate(new Date(attempts[0]?.updatedAt || attempts[0]?.endTime || attempts[0]?.createdAt))} />
                  ) : '-'}
                </Typography>
              </Box>
            </Box>

            {/* Attempts list */}
            {attempts.map((a, idx) => (
              <Accordion key={a.attemptId || idx} defaultExpanded={idx === 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1">Attempt #{attempts.length - idx}</Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Typography variant="body2">Score: {a.score ?? '-'}</Typography>
                      <Typography variant="body2">Time: {fmtSeconds(a.timeSpent)}</Typography>
                      {(() => {
                        const s = String(a?.status || '').toLowerCase();
                        let label = s || '-';
                        let colorOverride = colors.green[700];
                        let bgColorOverride = colors.green[100];
                        if (s === 'completed') {
                          label = 'completed';
                        } else if (s === 'in_progress' || s === 'in progress') {
                          label = 'in progress';
                          colorOverride = colors.Orange[400];
                          bgColorOverride = colors.Orange[100];
                        } else if (s === 'expired') {
                          label = 'expired';
                          colorOverride = colors.redAccent[600];
                          bgColorOverride = colors.redAccent[100];
                        }
                        return <RoleChip role={label} colorOverride={colorOverride} bgColorOverride={bgColorOverride} />;
                      })()}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <AttemptResponsesTable quizId={quizId} userId={userId} attemptId={a.attemptId} questionsIndex={questionsIndex} />
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReviewAllAttempts;

